import {ChangeDetectorRef, Component, enableProdMode, LOCALE_ID, NgZone, OnInit, ViewChild} from '@angular/core';
import { RouterModule} from '@angular/router';  // Remove RouterModule.forRoot()
import {HashLocationStrategy, LocationStrategy, CommonModule} from '@angular/common';
import {environment} from '../environments/environment';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {OfficeService} from '@core/services/office.service';
import {filter, take} from 'rxjs/operators';
import {LoadingPageComponent} from "@core/loading-page/loading-page.component";
import {WelcomeComponent} from "./public/authentication/welcome/welcome.component";
import {PinnableTaskpaneService} from '@core/services/pinnable-taskpane.service';
import {takeUntil, Subject} from 'rxjs';
import {JWTInterceptor} from "@core/auth/interceptor/jwt.interceptor";
import {AuthService} from '@core/auth/services/auth/auth.service';


export function getBaseUrl() {
	return document.getElementsByTagName('base')[0].href;
}

if (environment.production) {
	enableProdMode();
}

export function getApiUrl(locale = 'en-US') {
	if (!environment.production && environment.apiUrl) {
		return environment.apiUrl;
	}
	let url = document.getElementsByTagName('base')[0].href;
	if (locale !== 'en-US') {
		url = url.replace(`/${locale}/`, '/');
	}
	return url;
}

declare var Office: any;

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styles: [],
	standalone: true,
    imports: [RouterModule, CommonModule, LoadingPageComponent, WelcomeComponent],
	providers: [
		{provide: LocationStrategy, useClass: HashLocationStrategy}, // Use HashLocationStrategy, CustomHashLocationStrategy
		{provide: 'BASE_URL', useFactory: getBaseUrl, deps: []},
		{
			provide: LOCALE_ID,
			useValue: typeof $localize !== 'undefined' && $localize.locale ? $localize.locale : 'en-US'
		},
		{provide: 'API_URL', useFactory: getApiUrl, deps: [LOCALE_ID]},
		{provide: HTTP_INTERCEPTORS, useClass: JWTInterceptor, multi: true},
	],
})
export class AppComponent implements OnInit {
	isLoading = true;
	private destroy$ = new Subject<void>();

	constructor(
		private changeDetectorRef: ChangeDetectorRef,
		private officeService: OfficeService,
		private pinnableTaskpaneService: PinnableTaskpaneService,
		private authService: AuthService,
	) {
	}

	ngOnInit() {
		// Check if we're in Office context by looking for Office object
		const isInOfficeContext = typeof Office !== 'undefined';

		if (isInOfficeContext) {
			// Wait for Office to initialize with a minimum loading time
			const minimumLoadingTime = 5000; // 5 seconds minimum
			const startTime = Date.now();

			this.officeService.isInitialized
				.pipe(
					filter(initialized => initialized || initialized === false),
					take(1)
				)
				.subscribe(() => {
					const elapsedTime = Date.now() - startTime;
					const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

					if (remainingTime > 0) {
						// Wait for the remaining time before hiding loading
						setTimeout(() => {
							this.isLoading = false;
							this.changeDetectorRef.detectChanges();
							this.initializePinnableTaskpane();
						}, remainingTime);
					} else {
						this.isLoading = false;
						this.changeDetectorRef.detectChanges();
						this.initializePinnableTaskpane();
					}
				});
		} else {
			// Not in Office context, show loading for a brief moment for consistency
			setTimeout(() => {
				this.isLoading = false;
				this.changeDetectorRef.detectChanges();
			}, 5000);
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Initialize pinnable task pane functionality
	 */
	private initializePinnableTaskpane() {
		this.authService.rehydrateSession();
		console.log('=== APP COMPONENT: Initializing pinnable task pane functionality... ===');
		console.log('Office object available:', typeof Office !== 'undefined');
		console.log('Office service in context:', this.officeService.inOfficeContext);

		// Initialize the pinnable task pane service
		this.pinnableTaskpaneService.initialize();

		// Enable navigation to case list on email changes
		this.pinnableTaskpaneService.setNavigationBehavior(true);
		console.log('Navigation behavior set to true');

		// Subscribe to email change events for global handling
		this.pinnableTaskpaneService.emailChange$
			.pipe(takeUntil(this.destroy$))
			.subscribe((emailEvent) => {
				console.log('=== APP COMPONENT: Global email change event received:', emailEvent);
				// The service will handle navigation automatically
			});

		// Subscribe to pinned state changes
		this.pinnableTaskpaneService.isPinned$
			.pipe(takeUntil(this.destroy$))
			.subscribe((isPinned) => {
				console.log('=== APP COMPONENT: Global task pane pinned state:', isPinned);
			});

		console.log('=== APP COMPONENT: Pinnable task pane initialization complete ===');
	}
}
